import { useEffect, useState } from "react";
import { Award, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function CostCell({ m, onSaved }) {
  const [v, setV] = useState(m.cost ?? 0);
  const save = async () => { try { await api.patch(`/admin/milestones/${m.id}/cost`, { cost: Number(v) || 0 }); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return <input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} data-testid={`milestone-cost-${m.id}`} className="w-20 px-2 py-1 rounded-lg border border-stone-300 text-xs text-right" />;
}

function TemplateEditor({ templates, onSaved }) {
  const [t, setT] = useState(templates);
  const save = async () => { try { await api.put("/admin/milestone-templates", t); toast.success("Note templates saved"); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <details className="rounded-2xl border border-stone-200 p-4" data-testid="milestone-templates">
      <summary className="text-sm font-semibold text-stone-800 cursor-pointer">Edit the Rose City note templates</summary>
      {["century", "rooted"].map((k) => (
        <label key={k} className="block mt-3 text-xs font-semibold text-stone-600">{k === "century" ? "Century Club" : "Rooted (use {habit} for the habit name)"}
          <textarea value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} rows={4} data-testid={`milestone-template-${k}`} className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm font-normal text-stone-800" />
        </label>
      ))}
      <button onClick={save} data-testid="milestone-templates-save" className="amber-btn text-xs py-1.5 mt-3 inline-flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save templates</button>
    </details>
  );
}

export function MilestonesBoard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/milestones").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  return (
    <section className="warm-card p-6 space-y-4" data-testid="admin-milestones">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-rose-800"><Award className="w-3.5 h-3.5" /> Milestones</div>
          <p className="text-sm text-stone-600 mt-1">{data.counts.century} Century Club · {data.counts.rooted} Rooted. Budget so far: <strong data-testid="milestone-budget-total">${data.budget_total.toFixed(2)}</strong> (informational — nothing ships in phase 1).</p>
        </div>
      </div>
      {data.milestones.length === 0 ? <p className="text-sm text-stone-500">No milestones earned yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-stone-500"><th className="py-1 pr-3">Member</th><th className="pr-3">Milestone</th><th className="pr-3">Earned</th><th className="pr-3">Note</th><th className="pr-3">Coach line</th><th className="pr-3">On card</th><th className="text-right">Gift cost $</th></tr></thead>
            <tbody>
              {data.milestones.map((m) => (
                <tr key={m.id} className="border-t border-stone-100" data-testid={`admin-milestone-${m.id}`}>
                  <td className="py-2 pr-3"><span className="font-semibold text-stone-800">{m.name}</span><span className="block text-stone-500">{m.email}</span></td>
                  <td className="pr-3">{m.kind === "century" ? "Century Club" : `Rooted · ${m.habit_title}`}</td>
                  <td className="pr-3">{m.earned_at}</td><td className="pr-3">{m.note_status}</td>
                  <td className="pr-3">{m.clinician_line ? "yes" : "—"}</td><td className="pr-3">{m.show_on_card ? "yes" : "no"}</td>
                  <td className="text-right"><CostCell m={m} onSaved={load} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TemplateEditor templates={data.templates} onSaved={load} />
    </section>
  );
}
